import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "admin-view" }
const _hoisted_2 = { class: "d-flex justify-space-between align-center" }
const _hoisted_3 = { class: "text-body-1 mb-4" }

import { ref, computed, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { api } from '@/composables/api'
import { useAuthStore } from '@/store/auth'
import { useUserStore } from '@/store/users'
import SchoolsTable from '@/components/admin/SchoolsTable.vue'
import UsersTable from '@/components/admin/UsersTable.vue'
import BackupsTable from '@/components/admin/BackupsTable.vue'
import PendingUsersCard from '@/components/admin/PendingUsersCard.vue'
import CreateUserModal from '@/components/admin/CreateUserModal.vue'
import EditUserModal from '@/components/admin/EditUserModal.vue'
import MailerSettings from '@/components/admin/MailerSettings.vue'
import VolunteersTable from '@/components/admin/VolunteersTable.vue'
import NotifierCard from '@/components/admin/NotifierCard.vue'
import ConfigurationSettings from '@/components/admin/ConfigurationSettings.vue'


export default /*@__PURE__*/_defineComponent({
  __name: 'AdminView',
  setup(__props) {

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const userStore = useUserStore()
const loading = ref(true)
const activeTab = ref('users')
const backupsTableRef = ref<any>(null)

// Check if current user is admin (not just maintainer)
const isAdmin = computed(() => {
  return authStore.authUser && authStore.authUser.isAdmin()
})

// User modals state
const showCreateUserModal = ref(false)
const showEditUserModal = ref(false)
const editingUser = ref<any>(null)
const submittingUser = ref(false)
const userError = ref('')
const schools = ref<any[]>([])

// Snackbar state
const showSnackbar = ref(false)
const snackbarMessage = ref('')
const snackbarColor = ref('success')
const showDeleteUserModal = ref(false)
const userToDelete = ref<any>(null)
const deletingUser = ref(false)

const pendingUsers = computed(() => {
  return userStore.users.filter((user: any) => user.status === 'pending' || user.role === 'pending')
})

const isSelfEdit = computed(() => {
  if (!editingUser.value || !authStore.authUser) return false
  const editingUserId = editingUser.value._id || editingUser.value.id
  const currentUserId = authStore.authUser._id || authStore.authUser.id
  return editingUserId === currentUserId
})

const loadUsers = async () => {
  loading.value = true
  try {
    await userStore.fetchUsers()
    console.log('Users loaded:', userStore.users.length, 'users')
  } catch (error) {
    console.error('Error loading users:', error)
  } finally {
    loading.value = false
  }
}

const approveUser = async (user: any) => {
  try {
    await api.patch(`/users/${user.id}/approve`)
    await loadUsers()
    // Update pending count in auth store
    await authStore.fetchPendingUsersCount()
    showNotification('User approved successfully', 'success')
  } catch (error) {
    console.error('Error approving user:', error)
    showNotification('Failed to approve user', 'error')
  }
}

const rejectUser = async (user: any) => {
  const displayName = getUserDisplayName(user)
  if (confirm(`Are you sure you want to reject ${displayName}?`)) {
    try {
      await api.delete(`/users/${user.id}`)
      await loadUsers()
      // Update pending count in auth store
      await authStore.fetchPendingUsersCount()
      showNotification('User rejected', 'success')
    } catch (error) {
      console.error('Error rejecting user:', error)
      showNotification('Failed to reject user', 'error')
    }
  }
}

const getUserDisplayName = (user: any): string => {
  if (user.username) {
    return user.username
  }
  const firstName = user.first_name || user.first || ''
  const lastName = user.last_name || user.last || ''
  const fullName = `${firstName} ${lastName}`.trim()
  return fullName || user.email || 'Unknown User'
}

const showNotification = (message: string, color: string = 'success') => {
  snackbarMessage.value = message
  snackbarColor.value = color
  showSnackbar.value = true
}

// Load schools
const loadSchools = async () => {
  try {
    const response = await api.get('/schools')
    schools.value = response.data || response || []
  } catch (error) {
    console.error('Error loading schools:', error)
    schools.value = []
  }
}

// Create user modal handlers
const openCreateUserModal = () => {
  userError.value = ''
  showCreateUserModal.value = true
}

const closeCreateUserModal = () => {
  showCreateUserModal.value = false
  userError.value = ''
}

const createUser = async (userData: any) => {
  submittingUser.value = true
  userError.value = ''

  try {
    await api.post('/users', userData)
    await loadUsers()
    closeCreateUserModal()
    showNotification('User created successfully!', 'success')
  } catch (error: any) {
    console.error('Error creating user:', error)
    if (error.response?.status === 409) {
      userError.value = 'A user with that username or email already exists.'
    } else if (error.response?.status === 403) {
      userError.value = 'You do not have permission to create users.'
    } else {
      userError.value = error.response?.data?.message || 'Failed to create user. Please try again.'
    }
  } finally {
    submittingUser.value = false
  }
}

// Edit user modal handlers
const openEditUserModal = (user: any) => {
  console.log('openEditUserModal called with:', user)
  if (!user) {
    console.error('openEditUserModal: user is null or undefined!')
    return
  }
  editingUser.value = user
  userError.value = ''
  showEditUserModal.value = true
  console.log('Modal opened, editingUser set to:', editingUser.value)
}

const closeEditUserModal = () => {
  showEditUserModal.value = false
  userError.value = ''
  // Don't clear editingUser immediately to avoid prop validation warning
  // The modal will reset its own state when it closes
  setTimeout(() => {
    editingUser.value = null
  }, 300) // Wait for modal close animation
}

const updateUser = async (userData: any) => {
  if (!editingUser.value) return

  submittingUser.value = true
  userError.value = ''

  try {
    const userId = editingUser.value._id || editingUser.value.id
    await api.put(`/users/${userId}`, userData)

    // Refetch all users to get the latest data
    await loadUsers()

    closeEditUserModal()
    showNotification('User updated successfully!', 'success')
  } catch (error: any) {
    console.error('Error updating user:', error)
    if (error.response?.status === 409) {
      userError.value = 'A user with that username or email already exists.'
    } else if (error.response?.status === 403) {
      userError.value = 'You do not have permission to update users.'
    } else {
      userError.value = error.response?.data?.message || 'Failed to update user. Please try again.'
    }
  } finally {
    submittingUser.value = false
  }
}

// Delete user modal handlers
const openDeleteUserModal = (user: any) => {
  console.log('openDeleteUserModal called with:', user)
  if (!user) {
    console.error('openDeleteUserModal: user is null or undefined!')
    return
  }

  // Prevent deleting yourself
  const currentUserId = authStore.authUser._id || authStore.authUser.id
  const targetUserId = user._id || user.id
  if (currentUserId === targetUserId) {
    showNotification('You cannot delete your own account', 'error')
    return
  }

  // Prevent deleting root users
  if (user.role === 'root') {
    showNotification('Root users cannot be deleted', 'error')
    return
  }

  userToDelete.value = user
  showDeleteUserModal.value = true
}

const closeDeleteUserModal = () => {
  showDeleteUserModal.value = false
  setTimeout(() => {
    userToDelete.value = null
  }, 300)
}

const confirmDeleteUser = async () => {
  if (!userToDelete.value) return

  deletingUser.value = true

  try {
    const userId = userToDelete.value._id || userToDelete.value.id
    await api.delete(`/users/${userId}`)
    await loadUsers()
    closeDeleteUserModal()
    showNotification('User deleted successfully!', 'success')
  } catch (error: any) {
    console.error('Error deleting user:', error)
    if (error.response?.status === 403) {
      showNotification(error.response?.data?.detail || 'You do not have permission to delete this user.', 'error')
    } else {
      showNotification(error.response?.data?.detail || 'Failed to delete user. Please try again.', 'error')
    }
  } finally {
    deletingUser.value = false
  }
}

// Valid tab names
const validTabs = ['users', 'schools', 'volunteers', 'notifier', 'config', 'mailer', 'backups']

// Initialize active tab from URL query parameter
const initializeTab = () => {
  const tabFromQuery = route.query.tab as string
  if (tabFromQuery && validTabs.includes(tabFromQuery)) {
    // Check if user has permission for admin-only tabs
    if ((tabFromQuery === 'mailer' || tabFromQuery === 'backups' || tabFromQuery === 'config') && !isAdmin.value) {
      activeTab.value = 'users'
    } else {
      activeTab.value = tabFromQuery
    }
  }
}

// Update URL query when tab changes
watch(activeTab, (newTab) => {
  if (route.query.tab !== newTab) {
    router.replace({ query: { ...route.query, tab: newTab } })
  }
})

// Watch for URL changes (e.g., browser back/forward)
watch(() => route.query.tab, (newTab) => {
  if (newTab && validTabs.includes(newTab as string)) {
    // Check if user has permission for admin-only tabs
    if ((newTab === 'mailer' || newTab === 'backups' || newTab === 'config') && !isAdmin.value) {
      activeTab.value = 'users'
    } else if (activeTab.value !== newTab) {
      activeTab.value = newTab as string
    }
  }
})

onMounted(() => {
  initializeTab()
  loadUsers()
  loadSchools()
})

return (_ctx: any,_cache: any) => {
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_alert = _resolveComponent("v-alert")!
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_tab = _resolveComponent("v-tab")!
  const _component_v_tabs = _resolveComponent("v-tabs")!
  const _component_v_window_item = _resolveComponent("v-window-item")!
  const _component_v_window = _resolveComponent("v-window")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_card_title = _resolveComponent("v-card-title")!
  const _component_v_spacer = _resolveComponent("v-spacer")!
  const _component_v_card_actions = _resolveComponent("v-card-actions")!
  const _component_v_dialog = _resolveComponent("v-dialog")!
  const _component_v_snackbar = _resolveComponent("v-snackbar")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _cache[28] || (_cache[28] = _createElementVNode("div", { class: "page-header" }, [
      _createElementVNode("h1", null, "Administration"),
      _createElementVNode("p", null, "Manage users, schools, and database backups")
    ], -1)),
    (pendingUsers.value.length > 0)
      ? (_openBlock(), _createBlock(_component_v_alert, {
          key: 0,
          type: "warning",
          variant: "tonal",
          class: "mb-4",
          closable: ""
        }, {
          default: _withCtx(() => [
            _createElementVNode("div", _hoisted_2, [
              _createElementVNode("div", null, [
                _createElementVNode("strong", null, _toDisplayString(pendingUsers.value.length) + " pending user approval" + _toDisplayString(pendingUsers.value.length > 1 ? 's' : ''), 1)
              ]),
              _createVNode(_component_v_btn, {
                color: "warning",
                size: "small",
                onClick: _cache[0] || (_cache[0] = ($event: any) => (activeTab.value = 'users'))
              }, {
                default: _withCtx(() => [...(_cache[6] || (_cache[6] = [
                  _createTextVNode(" Review Now ", -1)
                ]))]),
                _: 1
              })
            ])
          ]),
          _: 1
        }))
      : _createCommentVNode("", true),
    _createVNode(_component_v_card, null, {
      default: _withCtx(() => [
        _createVNode(_component_v_tabs, {
          modelValue: activeTab.value,
          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((activeTab).value = $event)),
          "bg-color": "primary"
        }, {
          default: _withCtx(() => [
            _createVNode(_component_v_tab, { value: "users" }, {
              default: _withCtx(() => [
                _createVNode(_component_v_icon, { start: "" }, {
                  default: _withCtx(() => [...(_cache[7] || (_cache[7] = [
                    _createTextVNode("mdi-account-group", -1)
                  ]))]),
                  _: 1
                }),
                _cache[8] || (_cache[8] = _createTextVNode(" Users ", -1))
              ]),
              _: 1
            }),
            _createVNode(_component_v_tab, { value: "schools" }, {
              default: _withCtx(() => [
                _createVNode(_component_v_icon, { start: "" }, {
                  default: _withCtx(() => [...(_cache[9] || (_cache[9] = [
                    _createTextVNode("mdi-school", -1)
                  ]))]),
                  _: 1
                }),
                _cache[10] || (_cache[10] = _createTextVNode(" Schools ", -1))
              ]),
              _: 1
            }),
            _createVNode(_component_v_tab, { value: "volunteers" }, {
              default: _withCtx(() => [
                _createVNode(_component_v_icon, { start: "" }, {
                  default: _withCtx(() => [...(_cache[11] || (_cache[11] = [
                    _createTextVNode("mdi-account-heart", -1)
                  ]))]),
                  _: 1
                }),
                _cache[12] || (_cache[12] = _createTextVNode(" Volunteers ", -1))
              ]),
              _: 1
            }),
            _createVNode(_component_v_tab, { value: "notifier" }, {
              default: _withCtx(() => [
                _createVNode(_component_v_icon, { start: "" }, {
                  default: _withCtx(() => [...(_cache[13] || (_cache[13] = [
                    _createTextVNode("mdi-bell-ring", -1)
                  ]))]),
                  _: 1
                }),
                _cache[14] || (_cache[14] = _createTextVNode(" Notifier ", -1))
              ]),
              _: 1
            }),
            (isAdmin.value)
              ? (_openBlock(), _createBlock(_component_v_tab, {
                  key: 0,
                  value: "config"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_icon, { start: "" }, {
                      default: _withCtx(() => [...(_cache[15] || (_cache[15] = [
                        _createTextVNode("mdi-cog", -1)
                      ]))]),
                      _: 1
                    }),
                    _cache[16] || (_cache[16] = _createTextVNode(" Configuration ", -1))
                  ]),
                  _: 1
                }))
              : _createCommentVNode("", true),
            (isAdmin.value)
              ? (_openBlock(), _createBlock(_component_v_tab, {
                  key: 1,
                  value: "mailer"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_icon, { start: "" }, {
                      default: _withCtx(() => [...(_cache[17] || (_cache[17] = [
                        _createTextVNode("mdi-email-alert", -1)
                      ]))]),
                      _: 1
                    }),
                    _cache[18] || (_cache[18] = _createTextVNode(" SMTP ", -1))
                  ]),
                  _: 1
                }))
              : _createCommentVNode("", true),
            (isAdmin.value)
              ? (_openBlock(), _createBlock(_component_v_tab, {
                  key: 2,
                  value: "backups"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_icon, { start: "" }, {
                      default: _withCtx(() => [...(_cache[19] || (_cache[19] = [
                        _createTextVNode("mdi-database", -1)
                      ]))]),
                      _: 1
                    }),
                    _cache[20] || (_cache[20] = _createTextVNode(" Backups ", -1))
                  ]),
                  _: 1
                }))
              : _createCommentVNode("", true)
          ]),
          _: 1
        }, 8, ["modelValue"]),
        _createVNode(_component_v_card_text, null, {
          default: _withCtx(() => [
            _createVNode(_component_v_window, {
              modelValue: activeTab.value,
              "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((activeTab).value = $event))
            }, {
              default: _withCtx(() => [
                _createVNode(_component_v_window_item, { value: "users" }, {
                  default: _withCtx(() => [
                    _createVNode(PendingUsersCard, {
                      "pending-users": pendingUsers.value,
                      onApprove: approveUser,
                      onReject: rejectUser
                    }, null, 8, ["pending-users"]),
                    _createVNode(UsersTable, {
                      onCreate: openCreateUserModal,
                      onEdit: openEditUserModal,
                      onDelete: openDeleteUserModal
                    })
                  ]),
                  _: 1
                }),
                _createVNode(_component_v_window_item, { value: "schools" }, {
                  default: _withCtx(() => [
                    _createVNode(SchoolsTable)
                  ]),
                  _: 1
                }),
                _createVNode(_component_v_window_item, { value: "volunteers" }, {
                  default: _withCtx(() => [
                    _createVNode(VolunteersTable)
                  ]),
                  _: 1
                }),
                (isAdmin.value)
                  ? (_openBlock(), _createBlock(_component_v_window_item, {
                      key: 0,
                      value: "backups"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(BackupsTable, {
                          ref_key: "backupsTableRef",
                          ref: backupsTableRef
                        }, null, 512)
                      ]),
                      _: 1
                    }))
                  : _createCommentVNode("", true),
                _createVNode(_component_v_window_item, { value: "notifier" }, {
                  default: _withCtx(() => [
                    _createVNode(NotifierCard)
                  ]),
                  _: 1
                }),
                (isAdmin.value)
                  ? (_openBlock(), _createBlock(_component_v_window_item, {
                      key: 1,
                      value: "config"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(ConfigurationSettings)
                      ]),
                      _: 1
                    }))
                  : _createCommentVNode("", true),
                (isAdmin.value)
                  ? (_openBlock(), _createBlock(_component_v_window_item, {
                      key: 2,
                      value: "mailer"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(MailerSettings)
                      ]),
                      _: 1
                    }))
                  : _createCommentVNode("", true)
              ]),
              _: 1
            }, 8, ["modelValue"])
          ]),
          _: 1
        })
      ]),
      _: 1
    }),
    _createVNode(CreateUserModal, {
      show: showCreateUserModal.value,
      schools: schools.value,
      submitting: submittingUser.value,
      error: userError.value,
      onClose: closeCreateUserModal,
      onSubmit: createUser
    }, null, 8, ["show", "schools", "submitting", "error"]),
    _createVNode(EditUserModal, {
      show: showEditUserModal.value,
      user: editingUser.value,
      schools: schools.value,
      submitting: submittingUser.value,
      error: userError.value,
      "is-self-edit": isSelfEdit.value,
      onClose: closeEditUserModal,
      onSubmit: updateUser
    }, null, 8, ["show", "user", "schools", "submitting", "error", "is-self-edit"]),
    _createVNode(_component_v_dialog, {
      modelValue: showDeleteUserModal.value,
      "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((showDeleteUserModal).value = $event)),
      "max-width": "500px"
    }, {
      default: _withCtx(() => [
        _createVNode(_component_v_card, null, {
          default: _withCtx(() => [
            _createVNode(_component_v_card_title, { class: "text-h5" }, {
              default: _withCtx(() => [...(_cache[21] || (_cache[21] = [
                _createTextVNode(" Confirm Delete ", -1)
              ]))]),
              _: 1
            }),
            _createVNode(_component_v_card_text, null, {
              default: _withCtx(() => [
                _createElementVNode("p", _hoisted_3, [
                  _cache[22] || (_cache[22] = _createTextVNode(" Are you sure you want to delete user ", -1)),
                  _createElementVNode("strong", null, _toDisplayString(getUserDisplayName(userToDelete.value)), 1),
                  _cache[23] || (_cache[23] = _createTextVNode("? ", -1))
                ]),
                _createVNode(_component_v_alert, {
                  type: "warning",
                  variant: "tonal"
                }, {
                  default: _withCtx(() => [...(_cache[24] || (_cache[24] = [
                    _createTextVNode(" This action cannot be undone. The user will be permanently removed from the system. ", -1)
                  ]))]),
                  _: 1
                })
              ]),
              _: 1
            }),
            _createVNode(_component_v_card_actions, null, {
              default: _withCtx(() => [
                _createVNode(_component_v_spacer),
                _createVNode(_component_v_btn, {
                  color: "grey",
                  onClick: closeDeleteUserModal
                }, {
                  default: _withCtx(() => [...(_cache[25] || (_cache[25] = [
                    _createTextVNode("Cancel", -1)
                  ]))]),
                  _: 1
                }),
                _createVNode(_component_v_btn, {
                  color: "error",
                  onClick: confirmDeleteUser,
                  loading: deletingUser.value
                }, {
                  default: _withCtx(() => [...(_cache[26] || (_cache[26] = [
                    _createTextVNode("Delete", -1)
                  ]))]),
                  _: 1
                }, 8, ["loading"])
              ]),
              _: 1
            })
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["modelValue"]),
    _createVNode(_component_v_snackbar, {
      modelValue: showSnackbar.value,
      "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((showSnackbar).value = $event)),
      color: snackbarColor.value,
      timeout: 3000,
      location: "top"
    }, {
      actions: _withCtx(() => [
        _createVNode(_component_v_btn, {
          variant: "text",
          onClick: _cache[4] || (_cache[4] = ($event: any) => (showSnackbar.value = false))
        }, {
          default: _withCtx(() => [...(_cache[27] || (_cache[27] = [
            _createTextVNode(" Close ", -1)
          ]))]),
          _: 1
        })
      ]),
      default: _withCtx(() => [
        _createTextVNode(_toDisplayString(snackbarMessage.value) + " ", 1)
      ]),
      _: 1
    }, 8, ["modelValue", "color"])
  ]))
}
}

})